import { z } from 'zod';
import type { FeatureDefaults, FeatureFlags } from './feature-flags.zod';

/** Offres connues (FREE = essai / gratuit). */
export const CLIENT_PLAN_TYPES = ['FREE', 'BASIC', 'ADVANCED'] as const;

/**
 * Fiche client (espace /superadmin) : modification de l'abonnement d'un groupe.
 * Ne touche pas à la facturation Stripe : l'abonnement Stripe reste celui souscrit par le client.
 */
export const ClientSubscriptionUpdateSchema = z
  .object({
    /** Nouvelle date d'échéance (ISO). */
    date_echeance: z.string().datetime({ offset: true }).optional(),
    plan_type: z.enum(CLIENT_PLAN_TYPES).optional(),
    tier_id: z.string().min(1).max(50).optional(),
    /** -1 = illimité. */
    max_parcours_per_month: z.number().int().min(-1).max(1_000_000).optional(),
    /** Nombre maximal d'utilisateurs du groupe. */
    licence: z.number().int().min(1).max(10_000).optional(),
    /** Remet à zéro le compteur de parcours du mois. */
    reset_month_usage: z.boolean().optional(),
    /** Suggestions IA par mois pour ce client : null = quota global, 0 = illimité. */
    ai_monthly_limit: z.number().int().min(0).max(1_000_000).nullable().optional(),
  })
  .refine((value) => Object.values(value).some((v) => v !== undefined), {
    message: 'Aucune modification demandée',
  });
export type ClientSubscriptionUpdateInput = z.infer<typeof ClientSubscriptionUpdateSchema>;

export const ClientSuspendSchema = z.object({
  reason: z.string().trim().min(3, 'Indiquez la raison de la suspension').max(500),
});
export type ClientSuspendInput = z.infer<typeof ClientSuspendSchema>;

export interface SuperAdminClientUser {
  id: number;
  nom: string | null;
  prenom: string | null;
  email: string | null;
  phone_number: string | null;
  is_active: boolean | null;
  email_active: boolean | null;
  is_superadmin: boolean;
  profil: string | null;
  created_at: string | null;
}

export interface SuperAdminClientTraceEvent {
  id: string;
  email: string;
  stepKey: string;
  status: string;
  provider: string;
  errorMessage: string | null;
  createdAt: string;
}

export interface SuperAdminClientDetail {
  id: number;
  name: string | null;
  name_societe: string | null;
  siret: string | null;
  ville: string | null;
  cp: string | null;
  address_societe: string | null;
  groupe_type: string | null;
  created_at: string | null;
  plan_type: string | null;
  tier_id: string | null;
  subscription_status: string | null;
  date_subscribed: string | null;
  date_echeance: string | null;
  payment_failed_at: string | null;
  suspended_at: string | null;
  customer_id: string | null;
  sub_id: string | null;
  licence: number | null;
  current_month_parcours: number | null;
  max_parcours_per_month: number | null;
  forfaitName: string | null;
  counts: { users: number; activeUsers: number; inventaires: number };
  users: SuperAdminClientUser[];
  traceEvents: SuperAdminClientTraceEvent[];
  /** Consommation IA du mois en cours et quota applicable (plafond du client ou quota global, 0 = illimité). */
  ai: { usedThisMonth: number; limit: number; source: 'client' | 'global'; clientLimit: number | null };
  /** Exceptions du client et valeurs effectives des fonctionnalités activables. */
  features: { overrides: FeatureDefaults; effective: FeatureFlags };
}

/** Rapport mensuel de consommation IA (espace /superadmin). */
export interface SuperAdminAiUsageClient {
  groupeId: number | null;
  name: string;
  calls: number;
  errors: number;
  promptTokens: number;
  completionTokens: number;
  cost: number;
  limit: number;
  limitSource: 'client' | 'global';
  /** Part du quota consommée (0-1), null si illimité. */
  usageRatio: number | null;
  feedback: { useful: number; notUseful: number; applied: number };
}

export interface SuperAdminAiUsageReport {
  month: string;
  prices: { price_input_per_million: number; price_output_per_million: number };
  globalLimit: number;
  totals: { calls: number; errors: number; promptTokens: number; completionTokens: number; cost: number; clients: number };
  clients: SuperAdminAiUsageClient[];
}

export const AiUsageMonthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Mois attendu au format AAAA-MM');
